import React, { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faArrowLeft } from '@fortawesome/free-solid-svg-icons';

// Paces a fixed, known checklist of phases over the single real upload
// request — purely a UI animation, no fabricated backend data. Advances
// while `done` is false, capping at the last step (shown in-progress)
// until the parent flips `done` once the real response arrives.
export default function AnalyzingProgress({ steps, done, onBack }) {
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    if (done) {
      setActiveIndex(steps.length);
      return;
    }
    const interval = setInterval(() => {
      setActiveIndex(i => Math.min(i + 1, steps.length - 1));
    }, 700);
    return () => clearInterval(interval);
  }, [done, steps.length]);

  const handleBack = () => {
    if (onBack) onBack();
    else window.history.back();
  };

  return (
    <div style={s.page}>
      <button
        type="button"
        onClick={handleBack}
        style={s.backBtn}
        aria-label="Go back"
      >
        <FontAwesomeIcon icon={faArrowLeft} style={s.backIcon} />
        <span>Back</span>
      </button>

      <div style={s.content}>
        <div style={s.header}>
          <h1 style={s.title}>Analyzing your dataset…</h1>
          <p style={s.sub}>This only takes a moment.</p>
        </div>

        <ul style={s.list}>
          {steps.map((step, i) => {
            const complete = i < activeIndex;
            const active = i === activeIndex && !done;
            return (
              <li key={step} style={s.item}>
                <span
                  style={{
                    ...s.icon,
                    ...(complete ? s.iconDone : active ? s.iconActive : s.iconPending),
                  }}
                >
                  {complete ? (
                    <FontAwesomeIcon icon={faCheck} aria-hidden="true" />
                  ) : active ? (
                    <span style={s.spinner} />
                  ) : null}
                </span>
                <span
                  style={{
                    ...s.stepText,
                    ...(complete || active ? s.stepTextActive : {}),
                  }}
                >
                  {step}
                </span>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}

const s = {
  page: {
    display: 'flex',
    flexDirection: 'column',
    minHeight: '100vh',
    width: '100%',
    padding: '24px 20px 40px',
    boxSizing: 'border-box',
    background: 'var(--bg, #f8f7f4)',
    animation: 'fadeIn 0.3s ease',
  },
  backBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    alignSelf: 'flex-start',
    background: 'transparent',
    border: 'none',
    padding: '8px 10px',
    margin: '0 0 8px',
    cursor: 'pointer',
    fontSize: 14,
    fontWeight: 500,
    color: 'var(--text-muted)',
    borderRadius: 8,
    transition: 'color 0.15s ease, background 0.15s ease',
  },
  backIcon: {
    fontSize: 13,
  },
  content: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%',
    maxWidth: 480,
    margin: '0 auto',
    gap: 36,
  },
  header: {
    textAlign: 'center',
    width: '100%',
  },
  title: {
    fontSize: 26,
    fontWeight: 700,
    letterSpacing: '-0.025em',
    color: 'var(--text-primary)',
    lineHeight: 1.25,
    margin: 0,
  },
  sub: {
    fontSize: 15,
    color: 'var(--text-muted)',
    marginTop: 8,
    marginBottom: 0,
    lineHeight: 1.45,
  },
  list: {
    display: 'flex',
    flexDirection: 'column',
    gap: 14,
    width: '100%',
    margin: 0,
    padding: 0,
    listStyle: 'none',
  },
  item: {
    display: 'flex',
    alignItems: 'center',
    gap: 16,
    padding: '12px 16px',
    borderRadius: 12,
    background: 'rgba(255,255,255,0.55)',
    border: '1px solid rgba(0,0,0,0.04)',
    transition: 'background 0.2s ease',
  },
  icon: {
    width: 28,
    height: 28,
    borderRadius: '50%',
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 13,
    transition: 'background 0.2s ease, border-color 0.2s ease, transform 0.2s ease',
  },
  iconDone: {
    background: 'var(--success)',
    color: '#fff',
    border: '2px solid transparent',
  },
  iconActive: {
    background: 'var(--accent-subtle)',
    border: '2.5px solid var(--accent)',
  },
  iconPending: {
    background: 'transparent',
    border: '2px solid var(--border-subtle)',
  },
  spinner: {
    width: 11,
    height: 11,
    border: '2.5px solid var(--accent)',
    borderTopColor: 'transparent',
    borderRadius: '50%',
    animation: 'spin 0.7s linear infinite',
    display: 'inline-block',
  },
  stepText: {
    fontSize: 15.5,
    color: 'var(--text-muted)',
    lineHeight: 1.35,
    transition: 'color 0.2s ease, font-weight 0.15s ease',
  },
  stepTextActive: {
    color: 'var(--text-primary)',
    fontWeight: 600,
  },
};